import { createSelector } from 'reselect'
import { getCanViewLuxPlusBenefits, getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import { isCustomerSupportMode } from 'selectors/featuresSelectors'
import { earlyAccessRestrictedToMembers } from 'luxPlus/lib/utils'
import { getCurrentSiteTakeover } from 'takeovers/utils/hooks/useCurrentSiteTakeover'
import { LUX_PLUS_MONTH_TAKEOVER_ID } from 'takeovers/luxPlusMonthTakeover/constants/luxPlusMonthTakeoverConstants'

/**
 * If a user is not signed up to LuxPlus+, we should prevent them
  from seeing member-only offers until they become an active member or a free preview member.
 *
 * In place we display a locked offer UI.
 *
 * Exception: offers tagged with the LuxPlus+ Month campaign are shown unlocked to everyone,
 * regardless of membership — that campaign unblurs its own tagged member-only offers as a
 * promotional device, without affecting hidden offers anywhere else. Passing `offer` to
 * `getCurrentSiteTakeover` makes it verify the offer's campaignTags actually match the takeover's
 * own registered campaignTags (via isTakeoverApplicable), so a stale tag left on an offer after
 * the campaign ends doesn't keep unlocking it.
 */
export const getIsLuxPlusHiddenOffer = createSelector(
  (state: App.State) => getIsLuxPlusEnabled(state),
  (state: App.State) => getCanViewLuxPlusBenefits(state),
  (state: App.State) => isCustomerSupportMode(state),
  (state: App.State, offer?: App.AnyOffer) => getCurrentSiteTakeover(state, { offer })?.id === LUX_PLUS_MONTH_TAKEOVER_ID,
  (_state: App.State, offer?: App.AnyOffer) => offer,
  (isLuxPlusEnabled, canViewLuxPlusBenefits, isCustomerSupportMode, isLuxPlusMonthCampaignOfferApplicable, offer): boolean => {
    if (isLuxPlusMonthCampaignOfferApplicable) {
      return false
    }

    return isLuxPlusEnabled && !canViewLuxPlusBenefits && !isCustomerSupportMode && (offer?.luxPlus.access === 'memberOnly' || earlyAccessRestrictedToMembers(offer))
  },
)
